<script setup lang="ts">
import dayjs from 'dayjs';
import { useElementBounding } from '@vueuse/core';

// Composables
const { getInputColor } = useInput();
const { loadComparisonData, loadChronologicalData } = useChronologicalService();
const { formatNumberToIsoNumber } = useNumbers();
const { getTailwindColor } = useTailwind();

// Stores
const { areaId, period, aggregationLevel } = storeToRefs(exploreStore());
const { areasToCompare, loadingComparison, comparisonResponse, consumptionType } = storeToRefs(chronologicalStore());
const { setAreasToCompare } = chronologicalStore();

// Data
const comparisonList = ref<HTMLDivElement | null>(null);
const { height, update } = useElementBounding(comparisonList);
const differentStartTimes = ref(false);

const chartColors = ref<string[]>([
  getTailwindColor('rose-400'),
  getTailwindColor('blue-900'),
  getTailwindColor('orange-300'),
  getTailwindColor('rose-700'),
  getTailwindColor('violet-300'),
  getTailwindColor('emerald-700'),
  getTailwindColor('sky-300'),
  getTailwindColor('yellow-300'),
  getTailwindColor('pink-600'),
  getTailwindColor('emerald-300'),
]);

// Computed
const getDisabledAreas = computed(() => [areaId.value]);

// Methods
const addAreasToCompare = async (areas: { areaId: number; start?: Date }[]) => {
  consumptionType.value = 'consumption';
  const newAreasToCompare = areas.map((area, index) => ({
    id: area.areaId,
    start: area.start ? dayjs(area.start).format('YYYY-MM-DD HH:mm') : dayjs(period.value.start).format('YYYY-MM-DD HH:mm'),
    color: chartColors.value[index],
  }));
  await setAreasToCompare(newAreasToCompare);
  await loadComparisonData();
  update();
};

const deleteArea = async (areaId: number) => {
  await setAreasToCompare(areasToCompare.value.filter((area) => area.id !== areaId));
  await loadComparisonData();
};

const getCompareArea = (areaId: number) => {
  const areaCompareObject = areasToCompare.value.find((area) => area.id === areaId);
  const area = areaStore().getArea(areaId);
  if (!areaCompareObject || !area) return;

  return {
    ...areaCompareObject,
    ...area,
  };
};

const getConsumptionForArea = (areaId: number) => {
  const consumption = comparisonResponse.value?.compared_to.find((area) => area.area_id === areaId)?.consumption.total_consumption;
  return consumption?.quantity ?? 0;
};

const deleteAllAreas = () => {
  setAreasToCompare([]);
  loadChronologicalData();
};

const disabledDifferentStartTimes = () => {
  if (differentStartTimes.value) return;
  const newAreasToCompare = areasToCompare.value.map((area, index) => ({
    id: area.id,
    start: dayjs(period.value.start).format('YYYY-MM-DD HH:mm'),
    color: chartColors.value[index],
  }));
  setAreasToCompare(newAreasToCompare);
  loadComparisonData();
};

const getStartPeriod = (areaId: number) => {
  const area = areasToCompare.value.find((area) => area.id === areaId);
  if (!area) return;
  return dayjs(area.start).toDate();
};

const updateAreaStartPeriod = (areaId: number, event: Date | null | undefined) => {
  if (!event) return;
  const newAreasToCompare = areasToCompare.value.map((area, index) => ({
    id: area.id,
    start: area.id === areaId ? dayjs(event).format('YYYY-MM-DD HH:mm') : area.start,
    color: chartColors.value[index],
  }));
  setAreasToCompare(newAreasToCompare);
  loadComparisonData();
};

const getDiffBetweenAreaAndReference = (areaId: number) => {
  const reference = comparisonResponse.value?.reference;
  const area = comparisonResponse.value?.compared_to.find((item) => item.area_id === areaId);

  const referenceTotal = reference?.total_consumption.quantity ?? 0;
  const areaTotal = area?.consumption.total_consumption.quantity ?? 0;
  if (referenceTotal === 0) return { isPositive: true, value: 0 };

  const percentageDiff = ((areaTotal - referenceTotal) / referenceTotal) * 100;
  const value = formatNumberToIsoNumber(Math.abs(percentageDiff));
  return {
    isPositive: percentageDiff > 0,
    value,
  };
};
</script>
<template>
  <div class="h-[100%]">
    <div class="flex flex-col h-full">
      <div v-if="areasToCompare.length > 0" class="pb-4 border-b border-gray-200">
        <div class="flex flex-col items-center justify-between gap-3 transition-all duration-300">
          <nrjx-input-area
            v-if="areasToCompare.length > 0"
            side="left"
            :class="areasToCompare?.length > 0 ? 'opacity-100 delay-200' : 'opacity-0'"
            class="w-full"
            :area-ids="areasToCompare.map((area) => area.id).filter((id) => id !== undefined)"
            :area-disabled="getDisabledAreas"
            multiple
            @update:area-ids="$event ? addAreasToCompare($event.map((id) => ({ areaId: id }))) : null"
          >
            <template #button="{ open }">
              <div
                class="w-full px-2.5 h-[32px] text-sm rounded-md bg-white text-gray-600 flex items-center justify-between border border-gray-200"
                :class="getInputColor({ errorMessage: '', disabled: false, open: open })"
              >
                <span class="text-sm text-gray-600">{{ $t('global.area_to_compare') }}: {{ areasToCompare.length }}</span>
                <ui-icon name="ChevronDown" class="w-4 h-4 ml-4 stroke-gray-600" />
              </div>
            </template>
          </nrjx-input-area>

          <div
            v-if="areasToCompare.length > 0"
            class="flex items-center justify-between w-full"
            :class="areasToCompare?.length > 0 ? 'opacity-100 delay-200' : 'opacity-0'"
          >
            <nrjx-input-switch
              v-model="differentStartTimes"
              name="differentStartTimes"
              :label="$t('comparison.compare_differents_periods')"
              @update:model-value="disabledDifferentStartTimes"
            />
          </div>
        </div>
      </div>

      <!-- Liste of the areas to compare -->
      <div v-if="areasToCompare.length > 0" ref="comparisonList" class="h-[100%] flex-1">
        <div :style="{ height: `${height}px` }" class="flex flex-col overflow-y-auto py-2 gap-2">
          <div
            v-for="compareArea in areasToCompare.filter((area) => area.id !== undefined)"
            :key="compareArea.id"
            class="p-2 bg-gray-50 border border-gray-200 rounded-md flex flex-col items-start justify-between"
          >
            <div class="flex flex-row items-center justify-between w-full">
              <div class="flex items-center">
                <span class="text-xs text-white px-2 py-0.5 rounded-md font-semibold" :style="{ backgroundColor: compareArea.color }">
                  {{ getCompareArea(compareArea.id)?.name }}
                </span>

                <div v-if="!loadingComparison" class="flex items-center">
                  <span class="text-xs ml-3 block px-1.5 py-0.5 rounded-md text-gray-600">
                    {{ formatNumberToIsoNumber(getConsumptionForArea(compareArea.id)) }}
                    {{ ['hour', 'minute'].includes(aggregationLevel) ? 'kW' : 'kWh' }}
                  </span>
                  <span
                    class="text-xs ml-2 block px-1.5 py-0.5 rounded-md"
                    :class="
                      getDiffBetweenAreaAndReference(compareArea.id).isPositive ? 'text-red-500 bg-red-100' : 'text-green-500 bg-green-100'
                    "
                  >
                    {{ getDiffBetweenAreaAndReference(compareArea.id).isPositive ? '+' : '-' }}
                    {{ getDiffBetweenAreaAndReference(compareArea.id).value }}%
                  </span>
                </div>
                <ui-skeleton-loader v-else class="w-[70px] h-4 ml-3 rounded-full" />
              </div>
              <div v-if="!loadingComparison" class="p-1 hover:bg-gray-50 rounded-md cursor-pointer" @click="deleteArea(compareArea.id)">
                <ui-icon name="X" class="w-3.5 h-3.5 stroke-gray-600" />
              </div>
            </div>
            <nrjx-input-date
              v-if="differentStartTimes"
              class="w-full mt-2"
              name="period"
              :type="aggregationLevel === 'hour' || aggregationLevel === 'minute' ? 'datetime' : 'date'"
              :model-value="getStartPeriod(compareArea.id)"
              @update:model-value="updateAreaStartPeriod(compareArea.id, $event)"
            />
          </div>
          <div class="flex items-center justify-end my-2">
            <p class="text-xs text-gray-600 hover:text-gray-800 cursor-pointer" @click="deleteAllAreas">
              {{ $t('comparison.delete_all_areas') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Default message when no area is selected -->
      <div v-else class="flex flex-col items-center justify-center h-full w-full">
        <p class="text-sm text-gray-600 text-center mb-2">{{ $t('comparison.no_areas_selected') }}</p>
        <nrjx-input-area
          side="left"
          :area-disabled="getDisabledAreas"
          multiple
          @update:area-ids="$event ? addAreasToCompare($event.map((id) => ({ areaId: id }))) : null"
        >
          <template #button>
            <ui-button color="secondary" left-icon="Plus" class="w-full">{{ $t('comparison.add_area') }}</ui-button>
          </template>
        </nrjx-input-area>
      </div>
    </div>
  </div>
</template>
